import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Button,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Avatar,
  Tab,
  Tabs,
  Badge,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  useMediaQuery,
  Fab,
  Zoom,
  IconButton,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Menu,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  VpnKey as VpnKeyIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Link as LinkIcon,
  Replay as ReplayIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  Search as SearchIcon,
  HelpOutline as HelpOutlineIcon,
  CalendarToday as CalendarTodayIcon,
  Today as TodayIcon,
  WhatsApp as WhatsAppIcon,
  ContentCopy as ContentCopyIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  Timestamp,
  onSnapshot,
  getDocs,
  startAfter,
  startAt
} from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { EmhubAccessPass } from '../services/emhubAccessService';
import { generateAccessPass } from '../services/serverService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`access-pass-tabpanel-${index}`}
      aria-labelledby={`access-pass-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
};

interface EmhubAccessPassProps {
  onAccessUpdated?: () => void;
}

const EmhubAccessPasses: React.FC<EmhubAccessPassProps> = ({ onAccessUpdated }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [accessPasses, setAccessPasses] = useState<EmhubAccessPass[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [typeCounts, setTypeCounts] = useState({
    collection: 0,
    return: 0,
    others: 0,
    total: 0
  });
  
  // For search and filters
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchDate, setSearchDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [searchQrid, setSearchQrid] = useState('');
  const [searchType, setSearchType] = useState('');
  const [searchResults, setSearchResults] = useState<EmhubAccessPass[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // For pagination in "All Time" view
  const [lastVisible, setLastVisible] = useState<any>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // For caching
  const cacheRef = useRef<{
    today: EmhubAccessPass[];
    allTime: EmhubAccessPass[];
    todayDate: string | null;
    lastFetched: number;
  }>({
    today: [],
    allTime: [],
    todayDate: null,
    lastFetched: 0
  });
  
  // Keep track of listeners to clean them up
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // Add state for inline search in All Time tab
  const [allTimeSearchTerm, setAllTimeSearchTerm] = useState('');
  const [filteredPasses, setFilteredPasses] = useState<EmhubAccessPass[]>([]);

  // Add state for showing the iframe dialog
  const [iframeDialogOpen, setIframeDialogOpen] = useState(false);
  const [currentUrl, setCurrentUrl] = useState('');
  const [copied, setCopied] = useState(false);

  // Add state for the ad hoc access pass dialog
  const [adHocDialogOpen, setAdHocDialogOpen] = useState(false);
  const [adHocForm, setAdHocForm] = useState({
    type: '',
    customerName: '',
    contactNumber: '',
    validDate: new Date().toISOString().split('T')[0],
    pickupTime: '09:00',
    returnTime: '18:00'
  });
  const [adHocFormErrors, setAdHocFormErrors] = useState<Record<string, string>>({});
  const [adHocSubmitting, setAdHocSubmitting] = useState(false);
  const [adHocSuccess, setAdHocSuccess] = useState(false);

  // Add state for action menu
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPass, setSelectedPass] = useState<EmhubAccessPass | null>(null);

  // Format date for more readable display
  const formatDate = (dateString: string): string => {
    if (!dateString) return 'Invalid Date';
    
    // If the date is in DD/MM/YYYY format, parse it correctly
    const parts = dateString.split('/');
    if (parts.length === 3) {
      // Convert from DD/MM/YYYY to MM/DD/YYYY for proper Date parsing
      const date = new Date(`${parts[1]}/${parts[0]}/${parts[2]}`);
      if (!isNaN(date.getTime())) {
        return date.toLocaleString('en-US', {
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        });
      }
    }
    
    return 'Invalid Date';
  };

  // Helper functions for date manipulation
  const formatDateForComparison = (): string => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const isToday = (dateString: string): boolean => {
    if (!dateString) return false;
    return dateString === formatDateForComparison();
  };

  // Update the isOtherType function to handle exact type matching
  const isOtherType = (type: string | undefined): boolean => {
    if (!type) return true; // Consider empty/undefined type as "Other"
    return type !== 'Collection' && type !== 'Return';
  };

  // Replace the inline search with a database query
  const handleAllTimeSearch = (term: string) => {
    setAllTimeSearchTerm(term);
    
    if (!term.trim()) {
      setFilteredPasses(accessPasses);
      return;
    }
    
    // Set loading state to provide feedback
    setLoading(true);
    
    // Query the database directly instead of filtering local data
    const searchDatabase = async () => {
      try {
        // Perform a search query that doesn't limit results
        const searchQueryRef = query(
          collection(db, 'Emhub Access Pass'),
          orderBy('Date', 'desc')
        );
        
        const snapshot = await getDocs(searchQueryRef);
        
        if (!snapshot.empty) {
          const lowerTerm = term.toLowerCase();
          const results: EmhubAccessPass[] = [];
          
          snapshot.forEach((doc) => {
            const data = doc.data() as Record<string, any>;
            const pass = { id: doc.id, ...data } as EmhubAccessPass;
            
            // Filter results based on the search term
            if ((pass.VM && pass.VM.toLowerCase().includes(lowerTerm)) ||
                (pass.Qrid && pass.Qrid.toLowerCase().includes(lowerTerm)) ||
                (pass["Contact Number"] && pass["Contact Number"].toLowerCase().includes(lowerTerm)) ||
                (pass.Type && pass.Type.toLowerCase().includes(lowerTerm))) {
              results.push(pass);
            }
          });
          
          console.log(`Found ${results.length} matching results`);
          setFilteredPasses(results);
        } else {
          setFilteredPasses([]);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error performing search:', error);
        setFilteredPasses([]);
        setLoading(false);
      }
    };
    
    // Execute the search
    searchDatabase();
  };

  // Set up real-time listener for access passes
  const setupRealTimeUpdates = () => {
    try {
      // Clean up previous listener if it exists
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      
      setLoading(true);
      setError(null);
      
      // Get today's date in DD/MM/YYYY format
      const todayFormatted = formatDateForComparison();
      console.log('Filtering for date:', todayFormatted);
      
      // Check if we have cached data that's less than 5 minutes old
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000;
      const cacheExpired = (now - cacheRef.current.lastFetched) > fiveMinutes;
      
      if (tabValue === 0 && // Today's view
          cacheRef.current.today.length > 0 && 
          cacheRef.current.todayDate === todayFormatted && 
          !cacheExpired) {
        // Use cached data for today's view
        console.log('Using cached data for today view');
        const cachedPasses = cacheRef.current.today;
        
        // Count by type
        let collectionCount = 0;
        let returnCount = 0;
        let othersCount = 0;
        
        cachedPasses.forEach(pass => {
          if (pass.Type?.toLowerCase().includes('collection')) {
            collectionCount++;
          } else if (pass.Type?.toLowerCase().includes('return')) {
            returnCount++;
          } else {
            othersCount++;
          }
        });
        
        setAccessPasses(cachedPasses);
        setTypeCounts({
          collection: collectionCount,
          return: returnCount,
          others: othersCount,
          total: cachedPasses.length
        });
        setLoading(false);
        
        // Still set up a listener to catch real-time updates
      }
      
      if (tabValue === 1 && // All time view
          cacheRef.current.allTime.length > 0 &&
          !cacheExpired) {
        // Use cached data for all time view
        console.log('Using cached data for all time view');
        const cachedPasses = cacheRef.current.allTime;
        
        // Count by type
        let collectionCount = 0;
        let returnCount = 0;
        let othersCount = 0;
        
        cachedPasses.forEach(pass => {
          if (pass.Type?.toLowerCase().includes('collection')) {
            collectionCount++;
          } else if (pass.Type?.toLowerCase().includes('return')) {
            returnCount++;
          } else {
            othersCount++;
          }
        });
        
        setAccessPasses(cachedPasses);
        setTypeCounts({
          collection: collectionCount,
          return: returnCount,
          others: othersCount,
          total: cachedPasses.length
        });
        setLoading(false);
        
        // Get the last visible document for pagination
        if (cachedPasses.length > 0) {
          const lastDoc = cachedPasses[cachedPasses.length - 1];
          setLastVisible(lastDoc);
          setHasMore(true);
        }
      }
      
      // Create a query for access passes
      let accessQuery;
      
      if (tabValue === 0) { // Today's passes
        accessQuery = query(
          collection(db, 'Emhub Access Pass'),
          where('Date', '==', todayFormatted)
        );
      } else { // All passes with limit
        accessQuery = query(
          collection(db, 'Emhub Access Pass'),
          orderBy('Date', 'desc'),
          limit(25)
        );
      }
      
      // Set up real-time listener
      const unsubscribe = onSnapshot(
        accessQuery,
        (snapshot) => {
          console.log(`Received snapshot with ${snapshot.size} documents`);
          const fetchedPasses: EmhubAccessPass[] = [];
          let collectionCount = 0;
          let returnCount = 0;
          let othersCount = 0;
          
          if (!snapshot.empty) {
            snapshot.forEach((doc) => {
              const data = doc.data() as Record<string, any>;
              const pass = { id: doc.id, ...data } as EmhubAccessPass;
              console.log('Processing pass with date:', pass.Date);
              fetchedPasses.push(pass);
              
              // Count by type - updated logic for exact matches
              const type = pass.Type || '';
              if (type === 'Collection') {
                collectionCount++;
              } else if (type === 'Return') {
                returnCount++;
              } else {
                // This includes passes with empty/undefined Type or other non-exact types
                othersCount++;
              }
            });
            
            // Store the last document for pagination
            if (tabValue === 1 && fetchedPasses.length > 0) {
              setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
              setHasMore(true);
            }
          }
          
          console.log(`Found ${fetchedPasses.length} passes for ${tabValue === 0 ? 'today' : 'all time'}`);
          
          // Update cache
          if (tabValue === 0) {
            cacheRef.current.today = fetchedPasses;
            cacheRef.current.todayDate = todayFormatted;
          } else {
            cacheRef.current.allTime = fetchedPasses;
          }
          cacheRef.current.lastFetched = Date.now();
          
          setAccessPasses(fetchedPasses);
          setFilteredPasses(fetchedPasses); // Initialize filtered passes with all passes
          
          setTypeCounts({
            collection: collectionCount,
            return: returnCount,
            others: othersCount,
            total: fetchedPasses.length
          });
          setLoading(false);
          
          // Trigger the callback if provided
          if (onAccessUpdated) {
            onAccessUpdated();
          }
        },
        (error) => {
          console.error('Error in real-time updates:', error);
          setError('Failed to load access passes. Please try again.');
          setLoading(false);
        }
      );
      
      // Store the unsubscribe function
      unsubscribeRef.current = unsubscribe;
      
      return unsubscribe;
    } catch (error) {
      console.error('Error setting up real-time updates:', error);
      setError('Failed to set up updates. Please refresh the page.');
      setLoading(false);
      return () => {};
    }
  };
  
  // Load more data for the "All Time" view
  const loadMoreData = async () => {
    if (!lastVisible || loadingMore) return;
    
    try {
      setLoadingMore(true);
      
      const moreQuery = query(
        collection(db, 'Emhub Access Pass'),
        orderBy('Date', 'desc'),
        startAfter(lastVisible),
        limit(25)
      );
      
      const snapshot = await getDocs(moreQuery);
      
      if (snapshot.empty) {
        setHasMore(false);
        setLoadingMore(false);
        return;
      }
      
      const morePasses: EmhubAccessPass[] = [];
      
      snapshot.forEach((doc) => {
        const data = doc.data() as Record<string, any>;
        morePasses.push({ id: doc.id, ...data } as EmhubAccessPass);
      });
      
      // Update last visible
      setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
      
      // Combine with existing passes
      const updatedPasses = [...accessPasses, ...morePasses];
      setAccessPasses(updatedPasses);
      
      // Update cache
      cacheRef.current.allTime = updatedPasses;
      cacheRef.current.lastFetched = Date.now();
      
      // Recalculate type counts with updated logic
      let collectionCount = 0;
      let returnCount = 0;
      let othersCount = 0;
      
      updatedPasses.forEach(pass => {
        const type = pass.Type || '';
        if (type === 'Collection') {
          collectionCount++;
        } else if (type === 'Return') {
          returnCount++;
        } else {
          // This includes passes with empty/undefined Type or other non-exact types
          othersCount++;
        }
      });
      
      setTypeCounts({
        collection: collectionCount,
        return: returnCount,
        others: othersCount,
        total: updatedPasses.length
      });
      
      setLoadingMore(false);
    } catch (error) {
      console.error('Error loading more data:', error);
      setLoadingMore(false);
    }
  };
  
  // Handle search by date, Qrid, or type
  const searchAccessPasses = async () => {
    if (!searchDate && !searchQrid && !searchType) {
      return; // Don't search if no criteria provided
    }
    
    try {
      setSearchLoading(true);
      setSearchResults([]);
      
      let searchQuery: any;
      
      if (searchQrid) {
        // Search by Qrid
        searchQuery = query(
          collection(db, 'Emhub Access Pass'),
          where('Qrid', '==', searchQrid.trim()),
          orderBy('Timestamp', 'desc')
        );
      } else if (searchDate) {
        // Convert search date to DD/MM/YYYY format
        const searchDateObj = new Date(searchDate);
        const formattedSearchDate = `${String(searchDateObj.getDate()).padStart(2, '0')}/${String(searchDateObj.getMonth() + 1).padStart(2, '0')}/${searchDateObj.getFullYear()}`;
        
        searchQuery = query(
          collection(db, 'Emhub Access Pass'),
          where('Date', '==', formattedSearchDate),
          orderBy('Timestamp', 'desc')
        );
      } else {
        // Just use type filter
        searchQuery = query(
          collection(db, 'Emhub Access Pass'),
          orderBy('Timestamp', 'desc')
        );
      }
      
      const snapshot = await getDocs(searchQuery);
      
      if (!snapshot.empty) {
        const results: EmhubAccessPass[] = [];
        
        snapshot.forEach((doc) => {
          const data = doc.data() as Record<string, any>;
          const pass = { id: doc.id, ...data } as EmhubAccessPass;
          
          // Apply additional type filtering if specified
          if (!searchType || pass.Type?.includes(searchType)) {
            results.push(pass);
          }
        });
        
        setSearchResults(results);
      }
      
      setSearchLoading(false);
    } catch (err) {
      console.error('Error searching access passes:', err);
      setSearchLoading(false);
      setError('Failed to search. Please try again with different criteria.');
    }
  };
  
  // Set up listeners when component mounts or tab changes
  useEffect(() => {
    const unsubscribe = setupRealTimeUpdates();
    
    // Clean up listener when component unmounts
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [tabValue]);
  
  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Function to copy URL to clipboard
  const copyToClipboard = (url: string) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Function to open URL in iframe dialog
  const openInIframe = (pass: EmhubAccessPass) => {
    const url = pass["Emhub Access"];
    if (url) {
      setCurrentUrl(url);
      setIframeDialogOpen(true);
    }
  };

  // Function to open WhatsApp with pre-filled message
  const openWhatsApp = (pass: EmhubAccessPass) => {
    const message = `Hello 👋

Below access URL to enter As Easy As Rent at Emhub

${pass['Emhub Access']}

Access Valid Date:
${pass['Date']}

📍*EmHub Block B, Level 3A, No 38 & 39*

https://youtu.be/pxrlAIBNPBQ

🔑 EmHub Block B's Access Pass:
*Please generate only when you arrive at Gate as photo attached*

Thanks!`;

    // Create the WhatsApp URL with the message
    const whatsappURL = `https://api.whatsapp.com/send?phone=${pass['Contact Number']}&text=${encodeURIComponent(message)}`;
    
    // Open in a new window/tab
    window.open(whatsappURL, "_blank");
  };

  // Function to handle ad hoc form changes
  const handleAdHocFormChange = (field: string, value: string) => {
    setAdHocForm({
      ...adHocForm,
      [field]: value
    });
    
    // Clear error for this field if any
    if (adHocFormErrors[field]) {
      setAdHocFormErrors({
        ...adHocFormErrors,
        [field]: ''
      });
    }
  };

  // Function to validate the ad hoc form
  const validateAdHocForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!adHocForm.type) {
      errors.type = 'Please select a type';
    }
    
    if (!adHocForm.customerName) {
      errors.customerName = 'Name is required';
    }
    
    if (!adHocForm.contactNumber) {
      errors.contactNumber = 'Contact number is required';
    }
    
    if (!adHocForm.validDate) {
      errors.validDate = 'Valid date is required';
    }
    
    if (!adHocForm.pickupTime) {
      errors.pickupTime = 'Pickup time is required';
    }
    
    if (!adHocForm.returnTime) {
      errors.returnTime = 'Return time is required';
    }
    
    setAdHocFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Function to submit the ad hoc form
  const submitAdHocForm = async () => {
    if (!validateAdHocForm()) {
      return;
    }
    
    try {
      setAdHocSubmitting(true);
      
      // Create the request body for the server
      const requestBody = {
        customerName: adHocForm.customerName,
        contactNumber: adHocForm.contactNumber,
        validDate: adHocForm.validDate,
        pickupTime: adHocForm.pickupTime,
        returnTime: adHocForm.returnTime,
        type: adHocForm.type,
        qrid: '1000000000000' // Default QRID for ad hoc passes
      };
      
      // Submit to server
      const response = await generateAccessPass(requestBody);
      
      if (response.success) {
        console.log('Ad hoc access pass created successfully:', response);
        setAdHocSuccess(true);
        
        // Reset form after success
        setAdHocForm({
          type: '',
          customerName: '',
          contactNumber: '',
          validDate: new Date().toISOString().split('T')[0],
          pickupTime: '09:00',
          returnTime: '18:00'
        });
        
        // Close dialog after a short delay to show success message
        setTimeout(() => {
          setAdHocDialogOpen(false);
          setAdHocSuccess(false);
          
          // Refresh the access passes list
          setupRealTimeUpdates();
        }, 1500);
      } else {
        console.error('Failed to create ad hoc access pass:', response.error);
        setError(`Failed to create access pass: ${response.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error creating ad hoc access pass:', err);
      setError('Failed to create access pass. Please try again.');
    } finally {
      setAdHocSubmitting(false);
    }
  };

  // Function to handle opening the action menu
  const handleActionMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, pass: EmhubAccessPass) => {
    event.preventDefault();
    event.stopPropagation();
    console.log('Action menu button clicked for pass:', pass.id);
    setActionMenuAnchorEl(event.currentTarget);
    setSelectedPass(pass);
  };

  // Function to handle closing the action menu
  const handleActionMenuClose = () => {
    setActionMenuAnchorEl(null);
    setSelectedPass(null);
  };

  // Function to handle menu item click
  const handleActionMenuItemClick = (action: 'openLink' | 'copyLink' | 'whatsapp') => {
    if (!selectedPass) return;
    
    switch (action) {
      case 'openLink':
        window.open(selectedPass["Emhub Access"], "_blank");
        break;
      case 'copyLink':
        copyToClipboard(selectedPass["Emhub Access"]);
        break;
      case 'whatsapp':
        openWhatsApp(selectedPass);
        break;
    }
    
    handleActionMenuClose();
  };

  // Render a single access pass row - updated for better mobile display
  const renderAccessPassRow = (pass: EmhubAccessPass, isSearchResult: boolean = false) => {
    return (
      <TableRow 
        key={pass.id}
        onClick={() => openInIframe(pass)}
        sx={{ 
          cursor: 'pointer',
          '&:hover': { 
            bgcolor: 'rgba(0, 0, 0, 0.04)',
          }
        }}
      >
        <TableCell sx={{ 
          ...(isMobile && { 
            p: 1, 
            whiteSpace: 'normal',
            wordBreak: 'break-word'
          })
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar 
              sx={{ 
                width: isMobile ? 30 : 40, 
                height: isMobile ? 30 : 40, 
                bgcolor: pass.Type === 'Collection'
                  ? 'success.light' 
                  : pass.Type === 'Return'
                    ? 'info.light' 
                    : 'warning.light' 
              }}
            >
              {pass.Type?.charAt(0).toUpperCase() || '?'}
            </Avatar>
            <Box sx={{ ml: isMobile ? 1 : 2 }}>
              <Typography variant={isMobile ? "body2" : "body1"} fontWeight="medium">
                {pass.VM || 'N/A'}
              </Typography>
              <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary">
                {pass.Type || 'Other'}
              </Typography>
            </Box>
          </Box>
        </TableCell>
        {!isMobile && (
          <TableCell>
            <Tooltip title={pass["Contact Number"] || 'No contact number'}>
              <Box>
                <Typography variant="body2">
                  {pass["Contact Number"] || 'N/A'}
                </Typography>
              </Box>
            </Tooltip>
          </TableCell>
        )}
        <TableCell sx={{ ...(isMobile && { p: 1 }) }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AccessTimeIcon fontSize={isMobile ? "small" : "medium"} sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant={isMobile ? "caption" : "body2"}>
              {formatDate(pass.Date || '')}
            </Typography>
          </Box>
        </TableCell>
        <TableCell 
          onClick={(e) => e.stopPropagation()} 
          sx={{ 
            ...(isMobile && { 
              p: 1, 
              width: '60px',
              // Ensure the cell has enough height for the button
              minHeight: '50px'
            })
          }}
        >
          {isMobile ? (
            <IconButton
              size="medium" // Changed from small to medium for larger touch target
              onClick={(e) => handleActionMenuOpen(e, pass)}
              aria-label="actions menu"
              sx={{ 
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                padding: '8px', // Add more padding
                minWidth: '40px', // Set minimum width
                height: '40px', // Set fixed height
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <MoreVertIcon fontSize="medium" />
            </IconButton>
          ) : (
            <Box sx={{ 
              display: 'flex', 
              gap: 1,
              flexWrap: 'nowrap'
            }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<LinkIcon />}
                href={pass["Emhub Access"]}
                target="_blank"
                sx={{ borderRadius: 2 }}
              >
                ACCESS LINK
              </Button>
              
              <Button
                variant="outlined"
                size="small"
                startIcon={<ContentCopyIcon />}
                onClick={() => copyToClipboard(pass["Emhub Access"])}
                sx={{ 
                  borderRadius: 2,
                  borderColor: 'secondary.main',
                  '&:hover': {
                    borderColor: 'secondary.dark',
                    backgroundColor: 'rgba(156, 39, 176, 0.04)'
                  }
                }}
              >
                COPY URL
              </Button>
              
              {pass["Contact Number"] && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<WhatsAppIcon sx={{ color: '#25D366' }} />}
                  onClick={() => openWhatsApp(pass)}
                  sx={{ 
                    borderRadius: 2,
                    borderColor: '#25D366',
                    '&:hover': {
                      borderColor: '#128C7E',
                      backgroundColor: 'rgba(37, 211, 102, 0.04)'
                    }
                  }}
                >
                  WhatsApp
                </Button>
              )}
            </Box>
          )}
        </TableCell>
        {isSearchResult && !isMobile && (
          <TableCell>
            <Chip
              size="small"
              label={isToday(pass.Date || '') ? 'Today' : 'Past'}
              color={isToday(pass.Date || '') ? 'success' : 'default'}
              sx={{ borderRadius: 1 }}
            />
          </TableCell>
        )}
      </TableRow>
    );
  };
  
  return (
    <Box sx={{ mb: 4 }}>
      {/* Statistics Cards - made more mobile friendly */}
      <Grid container spacing={isMobile ? 1 : 3} sx={{ mb: isMobile ? 2 : 4 }}>
        <Grid item xs={6} sm={6} md={3}>
          <Card elevation={0} sx={{ 
            borderRadius: 2, 
            border: '1px solid', 
            borderColor: 'divider',
            ...(isMobile && { p: 1 })
          }}>
            <CardContent sx={{ p: isMobile ? 1 : 2, '&:last-child': { pb: isMobile ? 1 : 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} color="text.secondary">Total</Typography>
                <Avatar sx={{ 
                  bgcolor: 'primary.main',
                  width: isMobile ? 30 : 40,
                  height: isMobile ? 30 : 40
                }}>
                  <VpnKeyIcon fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
              </Box>
              <Typography variant={isMobile ? "h5" : "h4"} sx={{ mt: isMobile ? 1 : 2, fontWeight: 'bold' }}>
                {loading ? <CircularProgress size={isMobile ? 18 : 24} /> : typeCounts.total}
              </Typography>
              <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary">
                {tabValue === 0 ? "Today's Access Passes" : "All Access Passes"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={6} sm={6} md={3}>
          <Card elevation={0} sx={{ 
            borderRadius: 2, 
            border: '1px solid', 
            borderColor: 'divider',
            ...(isMobile && { p: 1 })
          }}>
            <CardContent sx={{ p: isMobile ? 1 : 2, '&:last-child': { pb: isMobile ? 1 : 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} color="text.secondary">Collection</Typography>
                <Avatar sx={{ 
                  bgcolor: 'success.main',
                  width: isMobile ? 30 : 40,
                  height: isMobile ? 30 : 40
                }}>
                  <CheckCircleIcon fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
              </Box>
              <Typography variant={isMobile ? "h5" : "h4"} sx={{ mt: isMobile ? 1 : 2, fontWeight: 'bold' }}>
                {loading ? <CircularProgress size={isMobile ? 18 : 24} /> : typeCounts.collection}
              </Typography>
              <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary">
                Collection Access Passes
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={6} sm={6} md={3}>
          <Card elevation={0} sx={{ 
            borderRadius: 2, 
            border: '1px solid', 
            borderColor: 'divider',
            ...(isMobile && { p: 1 })
          }}>
            <CardContent sx={{ p: isMobile ? 1 : 2, '&:last-child': { pb: isMobile ? 1 : 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} color="text.secondary">Return</Typography>
                <Avatar sx={{ 
                  bgcolor: 'info.main',
                  width: isMobile ? 30 : 40,
                  height: isMobile ? 30 : 40
                }}>
                  <ReplayIcon fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
              </Box>
              <Typography variant={isMobile ? "h5" : "h4"} sx={{ mt: isMobile ? 1 : 2, fontWeight: 'bold' }}>
                {loading ? <CircularProgress size={isMobile ? 18 : 24} /> : typeCounts.return}
              </Typography>
              <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary">
                Return Access Passes
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={6} sm={6} md={3}>
          <Card elevation={0} sx={{ 
            borderRadius: 2, 
            border: '1px solid', 
            borderColor: 'divider',
            ...(isMobile && { p: 1 })
          }}>
            <CardContent sx={{ p: isMobile ? 1 : 2, '&:last-child': { pb: isMobile ? 1 : 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} color="text.secondary">Others</Typography>
                <Avatar sx={{ 
                  bgcolor: 'warning.main',
                  width: isMobile ? 30 : 40,
                  height: isMobile ? 30 : 40
                }}>
                  <HelpOutlineIcon fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
              </Box>
              <Typography variant={isMobile ? "h5" : "h4"} sx={{ mt: isMobile ? 1 : 2, fontWeight: 'bold' }}>
                {loading ? <CircularProgress size={isMobile ? 18 : 24} /> : typeCounts.others}
              </Typography>
              <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary">
                Other Access Types
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Main Content */}
      <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            aria-label="access pass tabs"
            sx={{ px: isMobile ? 1 : 2 }}
            variant={isMobile ? "fullWidth" : "standard"}
          >
            <Tab 
              icon={<TodayIcon fontSize={isMobile ? "small" : "medium"} />} 
              iconPosition="start" 
              label="Today" 
              id="access-pass-tab-0"
              sx={{ fontSize: isMobile ? '0.75rem' : 'inherit' }}
            />
            <Tab 
              icon={<CalendarTodayIcon fontSize={isMobile ? "small" : "medium"} />} 
              iconPosition="start" 
              label="All Time" 
              id="access-pass-tab-1"
              sx={{ fontSize: isMobile ? '0.75rem' : 'inherit' }}
            />
          </Tabs>
          
          {/* Action buttons */}
          {!isMobile && (
            <Box sx={{ position: 'absolute', right: 16, top: 8 }}>
              <Button
                variant="outlined"
                startIcon={<SearchIcon />}
                size="small"
                onClick={() => setSearchOpen(true)}
                sx={{ mr: 1, borderRadius: 2 }}
              >
                Search
              </Button>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                size="small"
                onClick={setupRealTimeUpdates}
                sx={{ borderRadius: 2 }}
              >
                Refresh
              </Button>
            </Box>
          )}
        </Box>
        
        {/* Error message if any */}
        {error && (
          <Alert severity="error" sx={{ m: isMobile ? 1 : 2 }}>
            {error}
          </Alert>
        )}
        
        {/* Loading indicator */}
        {loading && <LinearProgress />}
        
        {/* Tab panels */}
        <TabPanel value={tabValue} index={0}>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size={isMobile ? "small" : "medium"}>
              <TableHead>
                <TableRow>
                  <TableCell>Access Pass</TableCell>
                  {!isMobile && <TableCell>Contact</TableCell>}
                  <TableCell>Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accessPasses.length > 0 ? (
                  accessPasses.map((pass) => renderAccessPassRow(pass))
                ) : (
                  <TableRow>
                    <TableCell colSpan={isMobile ? 3 : 4} align="center">
                      <Typography variant="body1" sx={{ py: 3 }}>
                        {loading ? 'Loading access passes...' : 'No access passes found for today.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Search by name, QRID, contact number, or type..."
              value={allTimeSearchTerm}
              onChange={(e) => handleAllTimeSearch(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                sx: { borderRadius: 2 }
              }}
            />
          </Box>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size={isMobile ? "small" : "medium"}>
              <TableHead>
                <TableRow>
                  <TableCell>Access Pass</TableCell>
                  {!isMobile && <TableCell>Contact</TableCell>}
                  <TableCell>Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && accessPasses.length > 0 ? (
                  (allTimeSearchTerm ? filteredPasses : accessPasses).map((pass) => renderAccessPassRow(pass))
                ) : (
                  <TableRow>
                    <TableCell colSpan={isMobile ? 3 : 4} align="center">
                      <Typography variant="body1" sx={{ py: 3 }}>
                        {loading ? 'Loading access passes...' : 
                         allTimeSearchTerm && filteredPasses.length === 0 ? 'No matching results found.' : 
                         'No access passes found.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                
                {/* Load More Row - only show if not filtering */}
                {tabValue === 1 && hasMore && accessPasses.length > 0 && !allTimeSearchTerm && (
                  <TableRow>
                    <TableCell colSpan={isMobile ? 3 : 4} align="center" sx={{ p: isMobile ? 1 : 2 }}>
                      <Button
                        variant="outlined"
                        onClick={loadMoreData}
                        disabled={loadingMore}
                        startIcon={loadingMore ? <CircularProgress size={16} /> : <RefreshIcon />}
                        sx={{ borderRadius: 2, fontSize: isMobile ? '0.75rem' : 'inherit' }}
                      >
                        {loadingMore ? 'Loading...' : 'Load More'}
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Paper>
      
      {/* Search Dialog */}
      <Dialog
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Search Access Passes
          <IconButton
            onClick={() => setSearchOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <RefreshIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mb: 3, mt: 1 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Date"
                type="date"
                value={searchDate}
                onChange={(e) => setSearchDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="QRID"
                value={searchQrid}
                onChange={(e) => setSearchQrid(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Type"
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
                fullWidth
                placeholder="Collection, Return, etc."
              />
            </Grid>
          </Grid>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setSearchDate('');
                setSearchQrid('');
                setSearchType('');
                setSearchResults([]);
              }}
              sx={{ mr: 1 }}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              onClick={searchAccessPasses}
              disabled={searchLoading}
              startIcon={searchLoading ? <CircularProgress size={16} /> : <SearchIcon />}
            >
              Search
            </Button>
          </Box>
          
          {/* Search Results */}
          {searchResults.length > 0 ? (
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Access Pass</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Actions</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchResults.map((pass) => renderAccessPassRow(pass, true))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              {searchLoading ? (
                <CircularProgress />
              ) : (
                searchDate || searchQrid || searchType ? (
                  <Typography>No results found. Try different search criteria.</Typography>
                ) : (
                  <Typography>Enter search criteria to find access passes.</Typography>
                )
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSearchOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      
      {/* IFrame Dialog */}
      <Dialog
        open={iframeDialogOpen}
        onClose={() => setIframeDialogOpen(false)}
        fullWidth
        maxWidth="lg"
        sx={{ 
          '& .MuiDialog-paper': { 
            height: '90vh',
            borderRadius: 2
          } 
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LinkIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Access Pass Preview</Typography>
          </Box>
          <Box>
            <IconButton 
              size="small" 
              onClick={() => copyToClipboard(currentUrl)}
              sx={{ mr: 1 }}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={() => window.open(currentUrl, '_blank')}
              sx={{ mr: 1 }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={() => setIframeDialogOpen(false)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {currentUrl && (
            <iframe
              src={currentUrl}
              title="Access Pass Preview"
              width="100%"
              height="100%"
              style={{ border: 'none' }}
            />
          )}
        </DialogContent>
      </Dialog>
      
      {/* Ad Hoc Access Pass Dialog */}
      <Dialog
        open={adHocDialogOpen}
        onClose={() => !adHocSubmitting && setAdHocDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="ad-hoc-access-dialog-title"
      >
        <DialogTitle id="ad-hoc-access-dialog-title">
          Generate Ad Hoc Access Pass
          <IconButton
            aria-label="close"
            onClick={() => !adHocSubmitting && setAdHocDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            disabled={adHocSubmitting}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {adHocSuccess ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
              <Typography variant="h6" color="success.main" gutterBottom>
                Access Pass Created Successfully!
              </Typography>
              <Typography variant="body1" color="text.secondary">
                The access pass has been generated and will appear in the list shortly.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <FormControl 
                  fullWidth 
                  error={!!adHocFormErrors.type} 
                  variant="outlined"
                >
                  <InputLabel id="ad-hoc-type-label">Type</InputLabel>
                  <Select
                    labelId="ad-hoc-type-label"
                    id="ad-hoc-type"
                    value={adHocForm.type}
                    onChange={(e) => handleAdHocFormChange('type', e.target.value)}
                    label="Type"
                    disabled={adHocSubmitting}
                  >
                    <MenuItem value="">
                      <em>Select a type</em>
                    </MenuItem>
                    <MenuItem value="Office Used">Office Used</MenuItem>
                    <MenuItem value="Delivery">Delivery</MenuItem>
                    <MenuItem value="Other Use Case">Other Use Case</MenuItem>
                  </Select>
                  {adHocFormErrors.type && (
                    <FormHelperText>{adHocFormErrors.type}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Name"
                  variant="outlined"
                  value={adHocForm.customerName}
                  onChange={(e) => handleAdHocFormChange('customerName', e.target.value)}
                  error={!!adHocFormErrors.customerName}
                  helperText={adHocFormErrors.customerName}
                  disabled={adHocSubmitting}
                  placeholder="Enter name or company"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Contact Number"
                  variant="outlined"
                  value={adHocForm.contactNumber}
                  onChange={(e) => handleAdHocFormChange('contactNumber', e.target.value)}
                  error={!!adHocFormErrors.contactNumber}
                  helperText={adHocFormErrors.contactNumber}
                  disabled={adHocSubmitting}
                  placeholder="e.g., 60123456789"
                />
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Valid Date"
                  type="date"
                  variant="outlined"
                  value={adHocForm.validDate}
                  onChange={(e) => handleAdHocFormChange('validDate', e.target.value)}
                  error={!!adHocFormErrors.validDate}
                  helperText={adHocFormErrors.validDate}
                  InputLabelProps={{ shrink: true }}
                  disabled={adHocSubmitting}
                />
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Pickup Time"
                  type="time"
                  variant="outlined"
                  value={adHocForm.pickupTime}
                  onChange={(e) => handleAdHocFormChange('pickupTime', e.target.value)}
                  error={!!adHocFormErrors.pickupTime}
                  helperText={adHocFormErrors.pickupTime}
                  InputLabelProps={{ shrink: true }}
                  disabled={adHocSubmitting}
                />
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Return Time"
                  type="time"
                  variant="outlined"
                  value={adHocForm.returnTime}
                  onChange={(e) => handleAdHocFormChange('returnTime', e.target.value)}
                  error={!!adHocFormErrors.returnTime}
                  helperText={adHocFormErrors.returnTime}
                  InputLabelProps={{ shrink: true }}
                  disabled={adHocSubmitting}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ bgcolor: 'background.default', p: 2, borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    QRID (Auto-filled)
                  </Typography>
                  <Typography variant="body1">
                    1000000000000
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    This is a default QRID used for ad hoc access passes.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        
        {!adHocSuccess && (
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button 
              onClick={() => setAdHocDialogOpen(false)} 
              disabled={adHocSubmitting}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button 
              onClick={submitAdHocForm} 
              disabled={adHocSubmitting}
              variant="contained"
              startIcon={adHocSubmitting ? <CircularProgress size={16} /> : null}
            >
              {adHocSubmitting ? 'Generating...' : 'Generate Access Pass'}
            </Button>
          </DialogActions>
        )}
      </Dialog>
      
      {/* Snackbar for copy notification */}
      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message="URL copied to clipboard!"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      
      {/* Only keep the Add button for floating actions */}
      <Box>
        {/* Ad Hoc Pass button (visible on all devices) */}
        <Zoom in={true}>
          <Fab
            color="secondary"
            sx={{ position: 'fixed', bottom: 16, right: 16 }}
            onClick={() => setAdHocDialogOpen(true)}
            size={isMobile ? "medium" : "large"}
          >
            <AddIcon />
          </Fab>
        </Zoom>
      </Box>
      
      {/* Action menu for mobile */}
      <Menu
        anchorEl={actionMenuAnchorEl}
        open={Boolean(actionMenuAnchorEl)}
        onClose={handleActionMenuClose}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          elevation: 3,
          sx: { 
            minWidth: 200,
            borderRadius: 2,
            '& .MuiList-root': {
              py: 0.5
            }
          }
        }}
      >
        <MenuItem onClick={() => handleActionMenuItemClick('openLink')}>
          <ListItemIcon>
            <LinkIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Open URL" />
        </MenuItem>
        <MenuItem onClick={() => handleActionMenuItemClick('copyLink')}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Copy URL" />
        </MenuItem>
        {selectedPass && selectedPass["Contact Number"] && (
          <MenuItem onClick={() => handleActionMenuItemClick('whatsapp')}>
            <ListItemIcon>
              <WhatsAppIcon fontSize="small" sx={{ color: '#25D366' }} />
            </ListItemIcon>
            <ListItemText primary="Send WhatsApp" />
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default EmhubAccessPasses; 